import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { authAPI } from '../../services/api';
import toast from '../../services/toast';
import { sanitizeAuthReturnTo } from '../../utils/authReturnTo';
import { AuthSplitShell } from '../../components/auth/AuthSplitShell';
import { authPrimaryButton } from '../../components/auth/authViewTheme';

const exchangeStorageKey = (code) => `googleAuthExchange:${code}`;

/**
 * Completes Sign in with Google after API callback redirects with ?code= or ?error=.
 */
export default function GoogleAuthComplete() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [message, setMessage] = useState('Completing sign-in…');
  const [done, setDone] = useState(false);

  useEffect(() => {
    const error = searchParams.get('error');
    const errorMessage = searchParams.get('message');
    const code = searchParams.get('code');
    const returnTo = sanitizeAuthReturnTo(searchParams.get('returnTo'));

    if (error) {
      setMessage(errorMessage || 'Google sign-in failed');
      setDone(true);
      toast.error(errorMessage || 'Google sign-in failed');
      return;
    }

    if (!code) {
      setMessage('Missing sign-in code. Try again from the login page.');
      setDone(true);
      return;
    }

    if (localStorage.getItem('token')) {
      navigate(returnTo || '/', { replace: true });
      return;
    }

    const storageKey = exchangeStorageKey(code);
    const prior = sessionStorage.getItem(storageKey);
    if (prior === 'done') {
      navigate(returnTo || '/', { replace: true });
      return;
    }
    if (prior === 'pending') {
      return;
    }
    sessionStorage.setItem(storageKey, 'pending');

    authAPI
      .exchangeGoogleLogin(code)
      .then(() => {
        sessionStorage.setItem(storageKey, 'done');
        toast.success('Signed in with Google');
        navigate(returnTo || '/', { replace: true });
      })
      .catch((err) => {
        sessionStorage.removeItem(storageKey);
        const msg = (err as Error).message || 'Could not complete Google sign-in';
        setMessage(msg);
        setDone(true);
        toast.error(msg);
      });
  }, [searchParams, navigate]);

  return (
    <AuthSplitShell
      title="Google sign-in"
      description={done ? message : 'Please wait while we finish signing you in.'}
    >
      {!done ? (
        <div className="flex flex-col items-center py-2">
          <Loader2 className="h-8 w-8 animate-spin text-brand-purple" aria-hidden />
          <p className="mt-4 text-center text-sm text-slate-400">{message}</p>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-sm text-slate-300">{message}</p>
          <Link to="/" className={`${authPrimaryButton} mt-6 inline-flex text-sm`}>
            Back to sign in
          </Link>
        </div>
      )}
    </AuthSplitShell>
  );
}
